import { useReducer, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useStreamSearch } from '@/store/searchApi';
import type { SearchDocumentsResult } from '@/types';
import { PageContent, PageHeader } from '@/components/layout/PageLayout';
import { StatusBanner } from '@/components/common/States';
import SearchBar from '@/components/search/SearchBar';
import SearchResults from '@/components/search/SearchResults';

interface SearchState {
  results: SearchDocumentsResult | undefined;
  answer: string;
  isLoading: boolean;
  isStreaming: boolean;
  hasSearched: boolean;
  error: string | null;
}

type SearchAction =
  | { type: 'SEARCH_STARTED' }
  | { type: 'RESULTS_RECEIVED'; data: SearchDocumentsResult }
  | { type: 'TOKEN_RECEIVED'; token: string }
  | { type: 'SEARCH_DONE' }
  | { type: 'SEARCH_ERROR'; error: string };

const initialState: SearchState = {
  results: undefined,
  answer: '',
  isLoading: false,
  isStreaming: false,
  hasSearched: false,
  error: null,
};

function searchReducer(state: SearchState, action: SearchAction): SearchState {
  switch (action.type) {
    case 'SEARCH_STARTED':
      return {
        ...state,
        hasSearched: true,
        isLoading: true,
        isStreaming: false,
        results: undefined,
        answer: '',
        error: null,
      };
    case 'RESULTS_RECEIVED':
      return {
        ...state,
        results: action.data,
        isLoading: false,
        isStreaming: action.data.llmEnabled ? true : state.isStreaming,
      };
    case 'TOKEN_RECEIVED':
      return {
        ...state,
        answer: state.answer + action.token,
      };
    case 'SEARCH_DONE':
      return {
        ...state,
        isLoading: false,
        isStreaming: false,
      };
    case 'SEARCH_ERROR':
      return {
        ...state,
        isLoading: false,
        isStreaming: false,
        results: undefined,
        error: action.error,
      };
  }
}

export default function SearchPage() {
  const { t } = useTranslation();
  const { streamSearch } = useStreamSearch();
  const [state, dispatch] = useReducer(searchReducer, initialState);

  const handleSearch = useCallback(
    async (query: string) => {
      dispatch({ type: 'SEARCH_STARTED' });

      await streamSearch({ query, top: 10 }, {
        onResults: (data) => {
          dispatch({ type: 'RESULTS_RECEIVED', data });
        },
        onToken: (token) => {
          dispatch({ type: 'TOKEN_RECEIVED', token });
        },
        onDone: () => {
          dispatch({ type: 'SEARCH_DONE' });
        },
        onError: () => {
          dispatch({ type: 'SEARCH_ERROR', error: t('search.error', 'An error occurred while searching. Please try again.') });
        },
      });
    },
    [streamSearch, t],
  );

  return (
    <PageContent>
      <PageHeader title={t('search.title')} subtitle={t('search.subtitle')} />
      <SearchBar onSearch={handleSearch} isSearching={state.isLoading} />
      {state.error && (
        <StatusBanner intent="danger">{state.error}</StatusBanner>
      )}
      <SearchResults
        data={state.results}
        isLoading={state.isLoading}
        hasSearched={state.hasSearched}
        answer={state.answer}
        isStreaming={state.isStreaming}
      />
    </PageContent>
  );
}
